/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T22:30:00+05:00
Intent: The receipt submissions list and review are answered in the browser until the gateway has actions for them.
*/
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * The ONLY backend this dashboard talks to: the LuQta microservice gateway.
 *
 * Xano is gone from the client. Every PublicApi key is either an action code in ACTION below,
 * or listed in NOT_ON_GATEWAY — a feature whose endpoint has no gateway action yet. Those never
 * leave the browser: a read answers with an empty result and a write fails with a clear message
 * (see `route`). PublicApi's URLs are kept only as names; nothing requests them.
 *
 * THE SCHEMA WAS REDESIGNED; THE FLOW AND THE PAYLOADS ARE XANO'S. So nothing in the
 * components changes: this file translates in both directions.
 *
 *   1. SHAPE. The microservice has ONE endpoint, POST /action { actioncode, data }, and wraps
 *      every reply as { isSuccess, data, message }. The components were written against Xano's
 *      bare bodies, so `data` is unwrapped and then reshaped PER KEY (see fromMicro) — an
 *      endpoint that returned a bare array in Xano still returns a bare array here.
 *
 *   2. TOKEN. The microservice token is kept under `msToken`, and is the only session there is.
 */

/** PublicApi key → microservice action code. Only keys that are actually BUILT appear. */
const ACTION: Record<string, number> = {
  // ── auth ────────────────────────────────────────────────────────────────
  SIGNUP: 1005, // Xano auth/signup              (740)
  SET_COMPANY_DETAILS: 1006, // Xano company_as_client        (944)
  SEND_OPT: 1007, // Xano auth/send-client-otp     (742)
  VERIFY_OPT: 1008, // Xano auth/verify-client-otp   (743)
  LOGIN: 1003, // Xano auth/login               (739)
  GET_ME: 1027, // Xano auth/me                  (741)
  SEND_RESET_LINK: 1028, // Xano forgot_password          (748)
  RESET_PASSWORD: 1029, // Xano reset-password           (749)
  USER_RESET_PASSWORD: 1030, // Xano auth/reset_password      (746)
  UPDATE_LANGUAGE: 1031, // Xano auth/language         (164312)
  CREATE_SUBUSER: 1032, // Xano auth/sub_user            (758)
  GET_SUBUSERS: 1033, // Xano auth/subusers            (759)

  // ── policy management: which pages each staff member may open ─────────────
  // Per-business policies. Every one of these is OWNER-only except MY_PAGES, which answers
  // about the caller alone and is what the sidebar asks to decide what to draw.
  POLICY_PAGES: 2361, // the pages a policy can grant
  POLICY_LIST: 2362, // this business's policies
  POLICY_SAVE: 2363, // create / rename, and set its pages
  POLICY_DELETE: 2364,
  POLICY_ASSIGN: 2365, // give a staff member a policy, or take it away
  POLICY_OF_USER: 2366, // one staff member's policy
  MY_PAGES: 2367, // what the signed-in user may open

  // ── Roles / Roles Management / Pages (Xano sub_users/client_roles & friends) ──
  // Ids are uuids. Create / update / delete need their own keys because one PublicApi key can
  // only map to one action (see CLIENT_ROLES_* and CLIENT_PAGES_* in public-api-constants.ts).
  CLIENT_ROLES: 2378, // Xano GET    client_roles
  CLIENT_ROLES_CREATE: 2379, // Xano POST   client_roles
  CLIENT_ROLES_UPDATE: 2380, // PATCH/PUT client_roles/{id}
  CLIENT_ROLES_DELETE: 2381, // DELETE client_roles/{id}
  CLIENT_ROLE_PERMISSIONS: 2382, // Xano GET    client_role_permissions?role_id
  CLIENT_ROLE_PERMISSIONS_BULK: 2383, // Xano POST   client_role_permissions_bulk
  CLIENT_PAGES: 2384, // Xano GET    client_pages
  CLIENT_PAGES_SAVE: 2385, // Xano POST   client_pages (create when no id, else edit)
  CLIENT_PAGES_DELETE: 2386, // Xano DELETE client_pages {id}
  SUBUSER_PERMISSIONS: 2387, // Xano GET    subuser_permissions (the role's grid, derived)
  MY_PERMISSIONS: 2389, // Xano GET    my_permissions
  // Existing actions reused: assigning is 2365, the team list is 1033.
  ASSIGN_SUBUSER_ROLE: 2365, // Xano POST   assign_subuser_role
  GET_SUB_USERS_BY_PARENT: 1033, // Xano GET    get_sub_users
  DELETE_SUBUSER: 1034, // Xano delete_subuser        (162261)
  UPDATE_SUBUSER: 2373, // Xano PUT update_subuser
  ACTIVATE_SUBUSER: 1036, // NEW  POST activate (email token, no JWT)
  VERIFY_SUBUSER_PASSWORD: 1037, // Xano verify_subuser_password (77965)
  UPDATE_SUBUSER_PASSWORD: 1038, // Xano update_subuser_password (77964)
  // The Settings / Profile screen's own data. Returned as a BARE ARRAY of one company because the
  // component reads response[0], exactly as Xano's all_company did.
  GET_USER_COMPANY: 1035, // Xano all_company            (946)

  // ── subscription ────────────────────────────────────────────────────────
  // The pricing page picks between these two by whether anyone is signed in:
  //   isLoggedIn() ? GET_SUBSCRIPTION_PLAN : GET_BILLING_PLANS
  // so the logged-out one MUST be the PUBLIC endpoint. Pointing it at 2300 (CLIENT) returned
  // UNAUTHENTICATED to every visitor and the page rendered an empty plan list.
  GET_SUBSCRIPTION_PLAN: 2300, // CLIENT — Xano active_plans     (87329)
  GET_BILLING_PLANS: 2400, // PUBLIC — Xano billing_payment (162277)
  // 2314, NOT 2302. Two different Xano endpoints, and this constant names the dashboard
  // one: client_dashboard/subscription_detail, which answers "how many engagements does
  // this plan allow, and how many are left". 2302 is the port of
  // subscription/user_subscription (820) — a different payload, with none of those keys in
  // it, which is why the card read 0/0 Left on a plan that allows twelve.
  SUBSCRIPTION_DETAIL: 2314, // Xano client_dashboard/subscription_detail
  // THE country list — one action for every screen in every app. 2315 still answers (from
  // the same helper) for anything not yet moved.
  GET_SUPPORTED_COUNTRIES: 2402, // the contest form's Location dropdown
  GLOBAL_LEVEL_SETTINGS: 2316, // the contest form's level bounds
  CHOOSE_SUBSCRIPTION_PLAN: 2304, // Xano subscribe_user     (814)
  FREE_PLAN: 2305, // Xano freePlan               (847)
  ENTERPRISE_PLAN: 2308, // Xano enterprise               (—)
  PURCHASED_PLAN: 2309, // Xano user/purchased_plan    (819)
  CHECK_PAYMENT: 2310, // Xano check_payment          (823)
  CHECK_PAYMENT_BY_USER: 2310, // Xano check_payment_by_user  (—)
  PAYMENT_REPORT: 2313, // Xano billing/report (828)
  // The Billing page's Discount History table, and the per-invoice discount the
  // PDF is enriched with. Both callers land here; see toMicro for the id.
  BILLING_DISCOUNT: 2317, // Xano get_discount_history
  INVOICE_DETAILS: 2371, // Xano GET   invoice_details — the invoice PDF download
  MY_SUBSCRIPTION: 2302, // Xano GET   user_subscription   (820)

  // ── Web QR plans (web-qr-subscription.service) ──────────────────────────
  // A separate product with its own table: buying or renewing one never touches the main
  // plan above, and the main plan never touches it. Plan ids are uuids.
  WEB_QR_PLANS: 2480, // Xano GET  web_qr/plans
  WEB_QR_SUBSCRIBE: 2481, // Xano POST web_qr/subscribe
  WEB_QR_RENEW: 2482, // Xano POST web_qr/renew
  WEB_QR_CONFIRM_PAYMENT: 2483, // Xano POST web_qr/confirm_payment
  WEB_QR_CASH_PAYMENT: 2484, // Xano POST web_qr/cash_payment
  WEB_QR_MY_SUBSCRIPTION: 2485, // Xano GET  web_qr/my_subscription
  SUBSCRIPTION_HISTORY: 2303, // Xano GET   plan_History        (844)
  CHANGE_PLAN: 2306, // Xano POST  sub/change_plan     (818)
  CANCEL_SUBSCRIPTION: 2307, // Xano PATCH subscription/{id}   (813)
  // The full dial-code list (signup, profile, OTP). The gateway's list is the platform's own
  // countries — the same rows GET_SUPPORTED_COUNTRIES reads.
  GET_COUNTRIES: 2402, // PUBLIC — Xano GET country

  // ── branches + Client QR registration (Branches screen) ───────────────────
  // The company is the token's; no client id is sent. Branch list / save / delete are three keys
  // on purpose: PublicApi's one CLIENT_BRANCHES key served GET, PUT and DELETE, and an ACTION
  // key can carry only one code.
  CLIENT_BRANCH_LIST: 2390, // Xano GET    client_dashboard/client_branches
  CLIENT_BRANCH_SAVE: 2391, // Xano POST   client_branches (create) + PUT client_branches/{id} (rename)
  CLIENT_BRANCH_DELETE: 2392, // Xano DELETE client_branches/{id}
  CLIENT_QR_REGISTER: 2393, // Xano POST   client_qr_register
  CLIENT_QR_MY_REGISTRATIONS: 2394, // Xano GET    client_qr/my_registrations

  // ── contests / engagements ──────────────────────────────────────────────
  TEST_PAGINATION: 2320, // Xano GET  get_contests      (contest_filter_test)
  GET_SINGLE_CONTEST: 2321, // Xano GET  contest/{id}
  GET_ALL_CONTESTS: 2322, // Xano GET  all_contests
  // Same endpoint, second caller: the dashboard and the engagements page both fill a contest
  // filter from Xano's ADMIN get_contests (77967) — which answered 401 for a client session.
  // That endpoint ignored its contest_id input and returned the caller's own contests only,
  // which is exactly what 2322 does.
  GET_DASHBOARD_CONTESTS: 2322, // Xano GET  get_contests (admin group, 77967)

  // The notification bell. Xano filtered its list in a lambda and returned nine cleaned
  // fields; 2331 keeps both the filter and those fields. Read/delivered state lives in
  // notification_deliveries now (Xano: notification_logs).
  GET_NOTIFICATION: 2331, // Xano GET  notif                        (995)
  UPDATE_NOTIFICATION: 2332, // Xano POST notification_update_client (997)
  MARK_ALL_NOTIFICATIONS_READ: 2333, // Xano POST mark_allnotifications_read (55168)

  // Renew. ONE endpoint, two routes: payment_method "online" returns a checkout_url and the
  // browser goes to Stripe; "cash" returns checkout_url: null and the component shows
  // "request submitted" — which is exactly the branch it already has.
  RENEWAL_PAYMENT_LINK: 2311, // Xano POST renewal_payment_link (85755)
  CASH_PAYMENT: 2312, // Xano POST cashpayment          (835)
  DASH_CONTEST_OVERVIEW: 2323, // Xano GET  Contest_Overview
  CONTEST_OVERVIEW: 2323, // Xano GET  contest_over_view
  DELETE_CONTEST: 2324, // Xano POST contest/remove
  // TWO DIFFERENT TAXONOMIES, and they were briefly pointed at the same code — which put
  // Sports / Food & Dining in the prize picker. CATEGORIES_LOOKUP is the PRIZE taxonomy
  // (Xano table 201, its own endpoint 48650); CONTEST_CATEGORY_LOOKUP is the contest one.
  CATEGORIES_LOOKUP: 2330, // Xano GET  categories_lookup   (48650)
  CONTEST_CATEGORY_LOOKUP: 2325, // Xano GET  user_interest
  // ONE endpoint for create AND update, branching on contest_id — exactly as Xano's
  // request_contest_client did. SAVE_CONTEST_DRAFT is the same call with status='draft';
  // in Xano it was a SECOND writer that skipped every plan and level check.
  REQUEST_CONTEST_CLIENT: 2326, // Xano POST request_contest_client (157174)
  SAVE_CONTEST_DRAFT: 2326, // Xano POST save_contest_draft      (106665)
  // Duplicate as draft — copied row for row on the server. Xano had no endpoint for it; the
  // panel read the contest back and re-submitted it through SAVE_CONTEST_DRAFT.
  DUPLICATE_CONTEST: 2388,
  // Both of Xano's deletes took an id and checked NOTHING else — any business could remove
  // another's level or prize by guessing. Scoped to the caller's own contest here.
  REQUEST_CONTEST_REMOVE_LEVEL: 2327, // Xano DELETE levels/{id}        (782)
  REQUEST_CONTEST_REMOVE_PRIZE: 2328, // Xano DELETE contest_prize/{id} (805)
  // The prize step's pool: per-prize stock/claim config and the redeem codes behind it.
  CONTEST_PRIZE_POOL: 2368, // Xano GET   contest_prize_pool/{contest_id}
  CONTEST_PRIZE_CONFIG: 2369, // Xano PATCH contest_prize_config/{contest_prize_id}
  CONTEST_PRIZE_CODES: 2370, // Xano POST  contest_prize_codes

  // ── vouchers ────────────────────────────────────────────────────────────
  // Stored-value vouchers the business issues (one, in bulk, or as a contest prize through 2369
  // with code_mode 'generated'). No Xano source: new with the redesigned schema.
  // 2395/2399 need contest.manage, 2396-2398/2319 contest.view. (2378-2383 are the Roles codes.)
  VOUCHER_CREATE: 2395, // issue one voucher (quantity 1) or a bulk batch
  VOUCHER_LIST: 2396, // the codes, with balance / uses / holder
  VOUCHER_BATCHES: 2397, // each issue, with counts and value totals
  VOUCHER_USAGE: 2398, // the spend ledger + per-currency totals
  VOUCHER_VOID: 2399, // void a voucher, a selection, or the unspent rest of a batch
  VOUCHER_CURRENCIES: 2319, // currencies a voucher can be issued in
  // The voucher budget: LuQta credits it in USD, every issue is charged against it.
  VOUCHER_BUDGET: 2496, // balance (USD + own currency), ledger, recent requests
  VOUCHER_BUDGET_REQUEST: 2497, // ask LuQta for budget, or withdraw the open request

  // ── the three that were still going to Xano ─────────────────────────────
  // These three keys existed in public-api-constants.ts and nowhere here, so every call fell
  // through to the Xano base URL — the components were unchanged, the requests just left the
  // platform. Adding the keys is the whole redirect: contest-type.component.ts,
  // add-contents.component.ts and constant-detail.component.ts keep calling the same names.
  //
  // 2334 is "created by Luqta": the business supplies dates, prize and objectives and the
  // request reaches the admin's Request → Contest tab as a lead.
  REQUEST_CONTEST_LUQTA: 2334,
  // 2349/2350 save levels or prizes on their own — the wizard's per-step save. Both run the
  // same write 2326 runs on the server, so a step save and a full save agree.
  REQUEST_CONTEST_SET_LEVEL: 2349,
  REQUEST_CONTEST_SET_PRIZE: 2350,
  // Publishing, which was the last piece of the contest flow still on Xano. Both keys land on
  // the same action: WEB_QR_SELF_APPROVE only ever existed because Xano's publish endpoint
  // demanded an SDK plan for EVERY publish, so a Web-access business had to be let in through a
  // side door. 2360 checks each surface against its own entitlement, so there is one door.
  PUBLISH_CONTEST: 2360,
  WEB_QR_SELF_APPROVE: 2360,

  // ── Participants ────────────────────────────────────────────────────────
  GET_ALL_PARTICIPANTS: 2335, // Xano GET all_participants / participants
  ALL_PARTICIPANTS: 2335, // Xano GET all_participants
  UNIQUE_PARTICIPANTS: 2336, // Xano GET unique_participants
  UNIQUE_CONTEST_PARTICIPANTS: 2337, // Xano GET unique_contest_participants
  UNIQUE_PARTICIPANTS_CONTEST_LEVELS: 2338, // Xano GET unique_participants_contest_levels
  CONTEST_LEVEL_COMPLETION_METRICS: 2339, // Xano GET contest_level_completion_metrics
  // The Participants screen calls THIS key, and it was the one missing from this map — so every
  // request for it left the platform for Xano, which holds no participation any more, and the
  // screen read "Participation Details (0)" for a contest two people had played. 2336 already
  // returns v2's exact shape.
  UNIQUE_PARTICIPANTS_V2: 2336, // Xano GET unique_participants_v2

  // ── the dashboard ───────────────────────────────────────────────────────
  // Six screens' worth of numbers that were still being read from Xano. Every participation,
  // level completion and winner is written to the redesigned database now, so those requests
  // succeeded and truthfully reported nothing — which is the four zero cards, the empty
  // Participation Data chart and the blank map. The contest detail screen failed louder: it
  // sends a contest uuid and Xano's input for it is declared `int contest_id`, which is the
  // "Value is not a valid integer" dialog.
  DASH_CONTEST_STATS: 2351, // Xano GET get_contest_stats
  DASH_PARTICIPANTS: 2352, // Xano GET participants_data
  DASH_LINE_CHART: 2353, // Xano GET line_chart
  DASH_COUNTRY_DATA: 2354, // Xano GET country_data
  CONTEST_DETAIL_OVERVIEW: 2355, // Xano GET contest_detail_overview
  // Receipts sent to the business's Receipt levels, by outcome. Gateway-only (no Xano twin).
  DASH_RECEIPT_STATS: 2356,
  CONTEST_LEADERBOARD: 2357, // Xano GET contest_leaderboard
  // The contest detail screen's participants and winners. Its shape repeats the whole
  // contest inside every participant, because extractWinners() reads the winner list off
  // participantsList[0].contest.contest_draw — flatten it and the Winners tab goes empty.
  GET_Total_PARTICIPANT: 2358, // Xano GET get_total_participates_client
  // Activity Logs. The rows are audit_events now — written by the gateway for every client
  // action — not the old backend's activity_log table, which nothing has written since the
  // migration.
  ACTIVITY_LOGS: 2359, // Xano GET log_activites/create_activity_log_for_client

  // ── Quiz Management ─────────────────────────────────────────────────────
  // The lookup is the one the CONTEST form uses to fill its quiz-level dropdown, which is why
  // it only offers active quizzes. Xano's get-by-id and delete never checked created_by, so any
  // business could read or delete another's quiz by guessing an id; both are scoped now.
  GET_QUIZZES: 2340, // Xano GET    quizzes_client         (48657)
  GET_QUIZ_BY_ID: 2341, // Xano GET    quizzes/{id}           (162292)
  QUIZ_UPSERT: 2342, // Xano POST   quiz_upset             (162293)
  DELETE_QUIZ: 2343, // Xano DELETE quizzes/{id}           (162294)
  GET_QUIZZES_LOOKUP: 2344, // Xano GET    quizzes_look_up_client (48659)

  // ── Web QR ───────────────────────────────────────────────────────────────
  // Real Xano flows: contest/qr_instance_list|create|update (178416/178415/178417).
  // WEB_QR_REPORT has no Xano source — greenfield, see ClientWebQr.service.ts.
  QR_INSTANCE_LIST: 2345, // Xano GET  contest/qr_instance_list   (178416)
  QR_INSTANCE_CREATE: 2346, // Xano POST contest/qr_instance_create (178415)
  QR_INSTANCE_UPDATE: 2347, // Xano POST contest/qr_instance_update (178417)
  WEB_QR_REPORT: 2348, // no Xano source

  // ── public reference data ───────────────────────────────────────────────
  // The company form loads this on init, mid-registration. It used to go to Xano with the
  // microservice token attached, which 401'd and read as an expired session.
  GET_SECTORS: 2401, // PUBLIC — Xano GET sector
  // The contest form's Business Objective dropdown. Platform-owned taxonomy: the client
  // reads it, the admin side edits it (8090-8092).
  GET_BUSINESS_SUBJECTS: 2329, // CLIENT — Xano GET business_subject (162286)

  // ── developer / SDK ────────────────────────────────────────────
  // 2372 READS the caller's keys. There is no action code for GENERATING one, and
  // developer.component has a second caller that does exactly that --
  // update('GET_DEVELOPER_API_KEY', { client_name }). Both callers share this key,
  // so Generate now reaches a read action and comes back with no api_key. It needs
  // its own code before that button works again; the list on page load does work.
  GET_DEVELOPER_API_KEY: 2372, // Xano GET third_party_user_api_keys
  SDK_APPLICATIONS: 2374, // Xano POST applications
  SDK_APPLICATIONS_LIST: 2375, // Xano GET  user_applications_list
  SDK_GENERATE_API_KEY: 2376, // Xano POST generate_api_key
  // Create with AI: the gateway checks the plan's AI entitlement and points, then calls ChatBolt.
  CONTEST_AI_GENERATE: 2377,
  // The AI assistant's signed context: { token, user_id, company_id, expires_at } for the
  // signed-in user and their business, so ChatBolt never has to trust a company_id from the page.
  CHATBOLT_TOKEN: 2486,

  // ── white-label branding + custom domains (a business's own brands and hostnames) ──
  // One action per key; ids are uuids. Every one is fenced to the signed-in business server-side.
  BRANDING_LIST: 2450, // GET  branding/list
  BRANDING_CREATE: 2451, // POST branding/create
  BRANDING_UPDATE: 2452, // POST branding/update (also the active toggle)
  BRANDING_SET_DEFAULT: 2453, // POST branding/set_default
  DOMAIN_LIST: 2454, // GET  domain/list
  DOMAIN_CREATE: 2455, // POST domain/create
  DOMAIN_UPDATE: 2456, // POST domain/update
  DOMAIN_VERIFY: 2457, // POST domain/verify (DNS TXT check)
  DOMAIN_DELETE: 2458, // POST domain/delete

  // ── uploads ─────────────────────────────────────────────────────────────
  UPLOAD_MODEL: 2459, // Xano POST upload/model — a .glb as a base64 data URI
  MY_MODELS: 2460, // Xano GET  my_models
  BASE_TO_IMAGE: 2461, // Xano POST baseToImage — base64 data URI in, hosted file object out
  DELETE_MODEL: 2462, // remove a model from the library (soft delete)

  // ── audit records (S1.3) — the caller's OWN campaigns; the server refuses any other ─────
  CONTEST_AUDIT_RECORDS: 2463, // a page of attempts, answers and verification
  CONTEST_AUDIT_EXPORT: 2464, // every record, for the export (recorded in the activity log)

  // ── campaign budget alerts (S2.3) — the caller's OWN campaigns ──────────────
  CONTEST_BUDGET_STATUS: 2465, // budget, spend and the alert level
  CONTEST_BUDGET_ALERT_SET: 2466, // the level (1-99%, empty = 80) at which the business is alerted

  // ── AI Insight: the report for a date range (E4.1) — the caller's OWN campaigns ──────────
  CLIENT_INSIGHT_REPORT: 2467, // template sentences linked to their figures, flags, recommendations

  // ── Live Dashboard: marketing periods, budget release, actions by objective ─────────
  MARKETING_PERIODS: 2489, // the business's periods + the currencies a budget can use
  MARKETING_PERIOD_SAVE: 2490, // create (no id) / update (id) a period
  MARKETING_PERIOD_DELETE: 2491, // remove a period — its campaigns are untouched
  MARKETING_OVERVIEW: 2492, // the cumulative dashboard for a period (empty period_id = all time)
  MARKETING_CAMPAIGN_DETAIL: 2493, // one campaign's dashboard
  MARKETING_OBJECTIVES: 2494, // the objectives a level can serve
  MARKETING_LEVEL_OBJECTIVE_SET: 2495, // a level's objective (empty = the level type's default)
  MARKETING_CAMPAIGNS: 2498, // one page of the campaign table: search, status, sort

  // ── winner override, web-QR instance participants ─────────────────────────
  // The admin's override (8155/8157) on the caller's OWN contests; the server refuses any other.
  WINNER_OVERRIDE: 2470, // POST winner_override
  WINNER_OVERRIDE_AUDIT_LOG: 2471, // GET  winner_override_audit_log
  WEB_QR_INSTANCE_PARTICIPANTS: 2472, // GET  web_qr_instance_participants

  // ── WhatsApp Campaign Companion (connections only — see NOT_ON_GATEWAY for the logs) ──
  COMPANION_NUMBERS_LIST: 2473,
  COMPANION_NUMBER_TOGGLE: 2474,
  COMPANION_NUMBER_REMOVE: 2475,
  COMPANION_IS_ENABLED: 2476,
  COMPANION_ENABLE: 2477,
  // The owner's per-member Companion switch. Read and write used to share one Xano URL, so the
  // write has its own key (SUBUSER_WHATSAPP_PERMISSION_UPDATE) — one key can only name one action.
  SUBUSER_WHATSAPP_PERMISSION: 2478,
  SUBUSER_WHATSAPP_PERMISSION_UPDATE: 2479,
  COMPANION_SET_ENABLED: 2479, // same action; target_user_id is the member

  // ── SDK payment return page ───────────────────────────────────────────────
  // The gateway has no separate SDK checkout: the SDK charge is a line on the plan's own invoice
  // (see sdkChargeFor), so confirming the payment is confirming that checkout session.
  CHECK_SDK_PAYMENT: 2310,

  // The client API key. 2372 lists the business's integration keys; only a hash is stored, so the
  // full key can never be read back (it is shown once, when generated, on the Developer page).
  CLIENT_API_KEY: 2372,

  // ── the legal pages (PUBLIC — shown before sign-in) ───────────────────────
  GET_PRIVACY_POLICY: 2403, // Xano GET privacy_policy — the active version, as a one-item array
  GET_TERMS_CONDITION: 2404, // Xano GET terms_condition
};

/**
 * Keys whose Xano endpoint has NO gateway action yet. They used to fall through to Xano; now
 * they never leave the browser (see `route`). The value is what a READ of the key answers with —
 * the empty form of what the screen reads — so the screen renders "nothing here" instead of an
 * error. A write always fails with NOT_ON_GATEWAY_MESSAGE.
 *
 * To bring a feature back, build its action and move the key into ACTION.
 */
const NOT_ON_GATEWAY: Record<string, any> = {
  // auth
  // Google sign-in for businesses stays OFF. A client sign-in would be a token-issuing action open
  // to the world, and nothing ever links a Google identity to a business account
  // (client_users.google_subject_id has no writer), so the only safe version has no account that
  // could ever match. The login page no longer loads Google Identity Services at all.
  SOCIAL_LOGIN: null,
  LOGOUT: {}, // no server-side session to end: the token is dropped locally
  // billing
  // Saved cards stay OFF: the gateway has no Stripe customer / SetupIntent flow and nothing writes
  // payment_methods, and the screens collect a raw card number and CVV, which must never reach
  // our server. The only safe version tokenises in the browser with Stripe.js — a project of its own.
  BANK_CARD: [],
  EDIT_BANK_CARD: null,
  DELETE_BANK_CARD: null,
  // team
  // No table backs a bulk permission write (the grants live per role, on 2383), and nothing calls it.
  SUBUSER_PERMISSIONS_BULK: null,
  // WhatsApp Campaign Companion — the activity feeds. The bot's message log and conversation/token
  // log are not stored anywhere in the database, so there is nothing to read. (The WhatsApp
  // Campaigns list is read from Xano directly by CompanionService.)
  COMPANION_LOGS: { items: [], itemsTotal: 0 },
  WHATSAPP_CONVERSATION_LOG: { items: [], itemsTotal: 0 },
  // The dashboard's Web QR dataset (qr_stats / qr_participants / qr_line_chart / qr_country_data).
  // The gateway has no action for any of the four yet, so the Web QR side of the dashboard's
  // Simple / Web QR switch reads empty. Each answer is the empty form of what the dashboard reads.
  DASH_QR_STATS: {},
  DASH_QR_PARTICIPANTS: { participants: [], itemsTotal: 0 },
  DASH_QR_LINE_CHART: [],
  DASH_QR_COUNTRY_DATA: [],
  // Receipt review on a campaign's detail page. The card is built; the gateway has no action to
  // list a receipt level's submissions or to approve / reject one yet. `available: false` tells
  // the card to say so instead of showing an empty list as if nobody had uploaded.
  CONTEST_RECEIPT_SUBMISSIONS: { items: [], total: 0, available: false },
  CONTEST_RECEIPT_REVIEW: null,
};

const NOT_ON_GATEWAY_MESSAGE = 'This feature is not available yet.';

/**
 * An id as an id, for components: a digits-only value stays a number (legacy rows), anything
 * else — a gateway uuid — stays a string. Number(uuid) is NaN, which JSON writes as null.
 */
export function asId(value: any): any {
  if (value === null || value === undefined || value === '') return value;
  const str = String(value);
  return /^-?\d+$/.test(str) ? Number(str) : str;
}

/**
 * The platform-webhook level's endpoint on the gateway (replaces Xano's luqtaWebhook). A partner
 * server POSTs JSON { contest_uid, level_uid, phone_number, type: 'app' } with
 * `Authorization: Bearer <developer API key>`.
 */
export const LUQTA_WEBHOOK_URL = `${environment.microUrl}/webhooks/luqta`;

/**
 * Stripe sends the buyer back to a FRESH page load, which resolves the gateway again from the URL
 * alone. On localhost that resolves to the deployed gateway unless ?gateway= is present, so a
 * checkout opened against another gateway (a local one while testing) came back and confirmed
 * against the wrong server — "Payment could not be confirmed", though the plan went live where it
 * was bought. The return urls therefore carry the gateway the checkout was opened on. Appended as
 * text, not through URL(): Stripe only fills in a literal {CHECKOUT_SESSION_ID}, and URL() would
 * percent-encode the braces.
 */
const DEPLOYED_GATEWAY = 'https://luqta-xano-api.xtroedge.com';
function withGatewayReturn(data: any): any {
  const gw = environment.microUrl;
  if (!data || typeof data !== 'object' || !gw || gw === DEPLOYED_GATEWAY) return data;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const tag = (u: any) =>
    typeof u === 'string' && origin && u.startsWith(origin) && !/[?&]gateway=/.test(u)
      ? `${u}${u.includes('?') ? '&' : '?'}gateway=${encodeURIComponent(gw)}`
      : u;
  if (!('success_url' in data) && !('cancel_url' in data)) return data;
  return {
    ...data,
    ...('success_url' in data ? { success_url: tag(data.success_url) } : {}),
    ...('cancel_url' in data ? { cancel_url: tag(data.cancel_url) } : {}),
  };
}

/** Where the microservice token lives. Deliberately not `jwtToken` — see the header. */
export const MS_TOKEN_KEY = 'msToken';

@Injectable({ providedIn: 'root' })
export class MicroApiService {
  private http = inject(HttpClient);

  /** True when this PublicApi key is served by the microservice. */
  handles(endpoint: string): boolean {
    return Object.prototype.hasOwnProperty.call(ACTION, endpoint);
  }

  /**
   * Every PublicApi request goes through here. A key with an action is called; any other key is
   * answered in the browser — `read` says which answer: the key's empty result for a read, a
   * NOT_ON_GATEWAY refusal for a write. Nothing is ever sent anywhere but the gateway.
   */
  route(endpoint: string, read: boolean, model?: any, extra?: any): Observable<any> {
    if (this.handles(endpoint)) return this.call(endpoint, model, extra);
    console.warn(`[MicroApi] ${endpoint} has no gateway action; not requested.`);
    if (read && endpoint in NOT_ON_GATEWAY && NOT_ON_GATEWAY[endpoint] !== null) {
      return of(structuredClone(NOT_ON_GATEWAY[endpoint]));
    }
    if (read && !(endpoint in NOT_ON_GATEWAY)) return of([]);
    return throwError(() => ({
      error: { message: NOT_ON_GATEWAY_MESSAGE, code: 'NOT_ON_GATEWAY' },
      status: 501,
      message: NOT_ON_GATEWAY_MESSAGE,
    }));
  }

  /** Call the microservice and hand back the shape the calling component already expects. */
  call(endpoint: string, model?: any, extra?: any): Observable<any> {
    const actioncode = ACTION[endpoint];
    // 2369 only writes. Its one read caller (getById, the last-resort prize-config fallback)
    // sends no body, and must not turn into a save — the pool (2368) already carries the config.
    if (endpoint === 'CONTEST_PRIZE_CONFIG' && (!model || typeof model !== 'object' || Object.keys(model).every((k) => k === 'id'))) {
      return throwError(() => ({ error: { message: 'Prize config is read through the prize pool' }, status: 400 }));
    }
    const data = withGatewayReturn(this.toMicro(endpoint, model, extra));

    // The gateway has ONE route — POST /action — and reads everything from the body, so in
    // DevTools every request in the Network list is called "action" and they are impossible to
    // tell apart. The action code and its key are appended to the URL purely so the Name column
    // reads `action?2300=GET_SUBSCRIPTION_PLAN`. The server ignores the query string entirely;
    // this changes nothing about the request other than making it identifiable.
    const label = `?${actioncode}=${endpoint}`;

    return this.http
      .post<any>(`${environment.microUrl}/action${label}`, { actioncode, data }, { headers: this.headers() })
      .pipe(
        map((res) => {
          if (res?.isSuccess === false) {
            // Xano signalled failure with an HTTP error carrying { message }; the components'
            // error handlers read it that way, so a business failure is re-thrown to match.
            // `data` rides along when a refusal carries one — a half-finished signup's login
            // answers NOT_VERIFIED / NO_COMPANY with the token needed to resume onboarding.
            throw {
              // `errors` is the list of reasons a refusal gives (the AI draft check's).
              error: {
                message: res.message,
                code: res.code,
                ...(res.data ? { data: res.data } : {}),
                ...(Array.isArray(res.errors) ? { errors: res.errors } : {}),
              },
              status: 400,
            };
          }
          this.captureToken(res?.data);
          return this.fromMicro(endpoint, res?.data ?? res);
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    // 1006/1007/1008 and every CLIENT code run on the token 1005 handed back — exactly as
    // Xano's `auth = "user"` did on those endpoints.
    const token = localStorage.getItem(MS_TOKEN_KEY);
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }

  /**
   * An id, as an id — never as a number.
   *
   * Every id in the redesigned schema is a uuid. These payloads used to coerce them with
   * Number(), which was right when ids were integers and is silently destructive now:
   * Number('4efe0389-6b9a-…') is NaN, and JSON.stringify writes NaN as `null`. So the browser
   * was sending `{ id: null }` and the endpoint answered "not found" for a row that was right
   * there. A digits-only id still goes out as a number, so nothing that genuinely counts on
   * one changes.
   */
  private idOf(...candidates: any[]): any {
    for (const c of candidates) {
      if (c === null || c === undefined || c === '') continue;
      if (typeof c === 'object') continue;
      const str = String(c);
      return /^-?\d+$/.test(str) ? Number(str) : str;
    }
    return null;
  }

  // ── request: component payload → what the microservice expects ─────────────
  private toMicro(endpoint: string, model: any, extra?: any): any {
    switch (endpoint) {
      case 'SET_COMPANY_DETAILS': {
        // business_logo and sector_id USED to be stripped here, which is why a saved profile
        // picture never appeared: it was dropped before the request left the browser. 1006 accepts
        // both now — the logo is uploaded and stored as a URL, the sectors go to client_sectors.
        //
        // The keys are only forwarded when the payload actually HAS them. The page sends
        // business_logo: null to mean "remove the picture", and the server can only tell that apart
        // from "unchanged" by whether the key is present at all — so an absent key must stay absent.
        const companyId = this.idOf(extra, model?.company_as_client_0_id, model?.id);
        const detailsValue = model?.details ?? model?.description ?? model?.address ?? model?.about;
        return {
          ...(companyId ? { id: companyId, company_as_client_0_id: companyId } : {}),
          name: model?.name,
          address: model?.address ?? detailsValue,
          details: detailsValue,
          description: detailsValue,
          legal_name: model?.legal_name ?? null,
          survey_visibility: model?.survey_visibility ?? null,
          ...('business_logo' in (model || {}) ? { business_logo: model.business_logo } : {}),
          ...('business_banner' in (model || {}) ? { business_banner: model.business_banner } : {}),
          ...('sector_id' in (model || {}) ? { sector_id: model.sector_id } : {}),
          ...('business_sector' in (model || {}) ? { business_sector: model.business_sector } : {}),
          // The owner's phone from the profile drawer; absent when the field was left empty.
          ...('phone_number' in (model || {}) ? { phone_number: model.phone_number } : (model?._user?.phone_number ? { phone_number: model._user.phone_number } : {})),
          ...('_user' in (model || {}) ? { _user: model._user } : {}),
          ...('user' in (model || {}) ? { user: model.user ?? model._user } : {}),
          ...('user_name' in (model || {}) ? { user_name: model.user_name ?? model._user?.name } : (model?._user?.name ? { user_name: model._user.name } : {})),
        };
      }

      case 'VERIFY_OPT':
        // The OTP screen posts { otp }, sometimes as a string; the service wants the number,
        // the same as Xano's `int otp`.
        return { otp: Number(model?.otp ?? model) };

      case 'SEND_OPT':
      case 'GET_ME':
      case 'PURCHASED_PLAN':
      case 'SUBSCRIPTION_DETAIL':
      case 'MY_SUBSCRIPTION':
      case 'SUBSCRIPTION_HISTORY':
      case 'GET_SUPPORTED_COUNTRIES':
      case 'GLOBAL_LEVEL_SETTINGS':
        return {}; // the token is the whole request

      case 'CHOOSE_SUBSCRIPTION_PLAN':
      case 'CHANGE_PLAN':
        return {
          plan_id: this.idOf(model?.plan_id, model?.planId, model?.id, model),
          type: model?.type ?? model?.billing_cycle ?? 'monthly',
          success_url: model?.success_url,
          cancel_url: model?.cancel_url,
        };

      // A client cancel always runs to period end; the server ignores `immediate`.
      case 'CANCEL_SUBSCRIPTION':
        return {
          ...(model?.subscription_id ? { subscription_id: this.idOf(model.subscription_id) } : {}),
          reason: model?.reason || null,
        };

      case 'FREE_PLAN':
        return { plan_id: this.idOf(model?.plan_id, model?.planId, model?.id, model) };

      case 'GET_SUBSCRIPTION_PLAN':
      case 'GET_BILLING_PLANS':
        return {
          billing_cycle: model?.billing_cycle ?? model?.type ?? undefined,
          include_enterprise: model?.include_enterprise ?? true,
        };

      // CHECK_SDK_PAYMENT also sent a user_id; the server confirms the SIGNED-IN business's
      // subscription and ignores any id in the body, so only the session goes.
      case 'CHECK_PAYMENT':
      case 'CHECK_PAYMENT_BY_USER':
      case 'CHECK_SDK_PAYMENT':
        return { session_id: model?.session_id ?? model?.sessionId ?? extra ?? undefined };

      // Winner override. The history is asked with getByCustomName('…', 'contest_id', <id>), so the
      // id arrives in `extra`; the write sends the three fields it was always sent.
      case 'WINNER_OVERRIDE_AUDIT_LOG':
        return { contest_id: this.idOf(extra, model?.contest_id) };
      case 'WINNER_OVERRIDE':
        return {
          contest_id: this.idOf(model?.contest_id),
          participant_id: this.idOf(model?.participant_id),
          reason: model?.reason ?? '',
        };

      // Companion. Ids are uuids, so they go through idOf, never Number().
      case 'COMPANION_NUMBER_TOGGLE':
        return { number_id: this.idOf(model?.number_id), companion_enabled: !!model?.companion_enabled };
      case 'COMPANION_NUMBER_REMOVE':
        return { number_id: this.idOf(model?.number_id) };
      case 'COMPANION_ENABLE':
        return { companion_enabled: !!model?.companion_enabled };
      case 'SUBUSER_WHATSAPP_PERMISSION_UPDATE':
        return { sub_user_id: this.idOf(model?.sub_user_id), companion_enabled: !!model?.companion_enabled };
      case 'COMPANION_SET_ENABLED':
        return {
          sub_user_id: this.idOf(model?.target_user_id),
          companion_enabled: !!model?.enabled,
          unlink: !!model?.unlink,
        };

      // Two callers, two fields, and the action reads them differently -- so both
      // go through untouched rather than being folded into one:
      //
      //   { billing_payment_id: 0 }      the whole history, for the table
      //   { subscription_id: <uuid> }    one subscription, for the invoice PDF
      //
      // Folding subscription_id into billing_payment_id looks reasonable (each
      // history row's `id` and `subscription_id` hold the same value) and
      // silently returns nothing: the action filters on subscription_id, and a
      // uuid under billing_payment_id matches no row. The PDF then showed no
      // discount at all.
      case 'BILLING_DISCOUNT':
        return model ?? {};

      case 'SDK_APPLICATIONS_LIST':
        return {}; // the token is the whole request

      case 'UPDATE_SUBUSER':
        // The edit drawer sends the same id three times over (subuser_id, user_id, id)
        // because the Xano endpoint's name for it was never settled. 2373 wants
        // subuser_id, so the duplicates are dropped here rather than in the component.
        //
        // The companion flags are NOT in 2373's declared input, and are forwarded
        // anyway: the drawer's Campaign Companion toggle is the only thing that
        // writes them, and dropping them would make that toggle quietly stop saving.
        // If the action rejects unknown keys, these are what to remove.
        return {
          subuser_id: this.idOf(model?.subuser_id, model?.user_id, model?.id, extra),
          name: model?.name,
          email: model?.email,
          phone_number: model?.phone_number,
          ...('status' in (model || {}) ? { status: model.status } : {}),
          ...('campaign_companion' in (model || {}) ? { campaign_companion: model.campaign_companion } : {}),
          ...('companion_access' in (model || {}) ? { companion_access: model.companion_access } : {}),
          ...('has_companion_access' in (model || {}) ? { has_companion_access: model.has_companion_access } : {}),
        };

      case 'SDK_APPLICATIONS':
        // The drawer's form is { name, platform, status }. `status` is not 2374's input
        // -- a new application is active by definition -- and bundle_id is accepted but
        // the form has no field for it yet.
        return {
          name: model?.name,
          platform: model?.platform ?? undefined,
          ...('bundle_id' in (model || {}) ? { bundle_id: model.bundle_id } : {}),
        };

      case 'DELETE_SUBUSER':
        // PagesService.delete puts the id in the URL; here it is a field.
        return { id: this.idOf(model?.id, model?.subuser_id, model, extra) };

      case 'ACTIVATE_SUBUSER':
        return { token: String(model?.token ?? model ?? '') };

      // Roles screens. The panel posts every alias of an id because Xano's binding was never
      // settled; the gateway wants one name.
      case 'CLIENT_ROLE_PERMISSIONS':
        return { role_id: this.idOf(model?.role_id, model?.id, extra) };
      case 'CLIENT_ROLES_UPDATE':
        return {
          id: this.idOf(extra, model?.id, model?.role_id),
          policy_name: model?.policy_name,
          description: model?.description,
        };
      case 'CLIENT_ROLES_DELETE':
        return { id: this.idOf(extra, model?.id, model?.role_id, model) };
      case 'CLIENT_PAGES_DELETE':
        return { id: this.idOf(model?.id, extra, model) };
      case 'SUBUSER_PERMISSIONS':
        return { sub_user_id: this.idOf(model?.sub_user_id, model?.id, extra) };
      case 'MY_PERMISSIONS':
        return {};
      // 2365 takes the member as client_user_id and unassigns with a null role; the panel uses 0.
      case 'ASSIGN_SUBUSER_ROLE': {
        const role = this.idOf(model?.role_id, model?.client_role_id, model?.client_roles_id);
        return {
          client_user_id: this.idOf(model?.sub_user_id, model?.subuser_id, model?.user_id, model?.id),
          role_id: role === 0 || role === '0' ? null : role,
        };
      }
      // 1033 lists the CALLER's own team (the token says whose), so parent_id is dropped; it
      // caps per_page at 100.
      case 'GET_SUB_USERS_BY_PARENT': {
        const out: any = {};
        if (model?.page !== undefined) out.page = Number(model.page) || 1;
        if (model?.per_page !== undefined) out.per_page = Math.min(Number(model.per_page) || 100, 100);
        return out;
      }

      // getById(key, id) appends /{id} to the Xano URL, so the id arrives in `extra`.
      case 'GET_SINGLE_CONTEST':
        return { id: this.idOf(extra, model?.id, model) };

      // getById(key, id) again, but the field is named for what it is: the id in the URL
      // was the CONTEST's, not the prize pool row's.
      case 'CONTEST_PRIZE_POOL':
        return { contest_id: this.idOf(extra, model?.contest_id, model?.id, model) };

      // patch(key, id, body) put the prize id in the URL, so it arrives in `extra` and
      // the settings -- prize_kind, code_mode, quantity, voucher_code,
      // redeem_instructions, expires_at -- arrive in `model`. Both go out together.
      case 'CONTEST_PRIZE_CONFIG':
        return {
          ...(model ?? {}),
          contest_prize_id: this.idOf(extra, model?.contest_prize_id, model?.id),
        };

      // Already { contest_prize_id, codes } / { subscription_id } / { application_id }
      // -- nothing to reshape.
      case 'CONTEST_PRIZE_CODES':
      case 'INVOICE_DETAILS':
      case 'SDK_GENERATE_API_KEY':
        return model ?? {};

      // Reads the caller's keys, so there is nothing to send. `client_name` from the
      // Generate caller is forwarded rather than dropped, so that the day this key
      // gets a create action the payload is already arriving intact.
      case 'GET_DEVELOPER_API_KEY':
        return model ?? {};

      // deletebyId(key, id) appends /{id} to the Xano URL, so the id arrives in `extra`.
      case 'REQUEST_CONTEST_REMOVE_LEVEL':
      case 'REQUEST_CONTEST_REMOVE_PRIZE':
        return { id: this.idOf(extra, model?.id, model) };

      // getById(key, id) for the edit drawer, deleteCard(key, id) for the delete button — both
      // put the id in the URL on Xano, so it arrives in `extra` here.
      case 'GET_QUIZ_BY_ID':
      case 'DELETE_QUIZ':
        return { id: this.idOf(extra, model?.id, model) };

      // The whole nested quiz goes through as-is: quiz_title, description, dates, is_active and
      // questions[] each with options[]. The endpoint branches on `id` for create vs update,
      // exactly as Xano's quiz_upset did.
      case 'QUIZ_UPSERT':
        return model ?? {};

      case 'DELETE_CONTEST':
        return { contest_id: this.idOf(model?.contest_id, model?.id, model) };

      // The contest list and the stat strip pass their filters as query params, which
      // PagesService.getAll turns into HttpParams and this hands straight through — the
      // endpoint reads the same names Xano's get_contests declared.
      case 'TEST_PAGINATION':
      case 'PAYMENT_REPORT':
      case 'ALL_PARTICIPANTS':
      case 'GET_ALL_PARTICIPANTS':
      case 'UNIQUE_PARTICIPANTS':
      case 'UNIQUE_CONTEST_PARTICIPANTS':
      case 'UNIQUE_PARTICIPANTS_CONTEST_LEVELS':
      case 'CONTEST_LEVEL_COMPLETION_METRICS':
      case 'DASH_CONTEST_OVERVIEW':
      case 'CONTEST_OVERVIEW':
      // The dashboard's filters go on the wire under the names Xano declared, so the components
      // keep sending exactly what they sent — contest_id, start_date/end_date,
      // participate_start_date/participate_end_date, days_range, page/per_page. Including
      // `contest_id=0`, which is how these screens say "no contest filter"; the services read
      // it as absent rather than as a bad id.
      case 'UNIQUE_PARTICIPANTS_V2':
      case 'DASH_CONTEST_STATS':
      case 'DASH_PARTICIPANTS':
      case 'DASH_LINE_CHART':
      case 'DASH_COUNTRY_DATA':
      case 'DASH_RECEIPT_STATS':
      case 'CONTEST_DETAIL_OVERVIEW':
      case 'CONTEST_LEADERBOARD':
        return model ?? {};

      case 'GET_ALL_CONTESTS':
      case 'GET_DASHBOARD_CONTESTS':
      case 'GET_USER_COMPANY':
      case 'GET_NOTIFICATION':
      case 'GET_BUSINESS_SUBJECTS':
      case 'CATEGORIES_LOOKUP':
      case 'CONTEST_CATEGORY_LOOKUP':
      case 'GET_QUIZZES':
      case 'GET_QUIZZES_LOOKUP':
        return {};

      // The whole create/edit payload goes through as-is. The endpoint accepts the level array
      // under either name — the draft path renames level_details to _levels_of_contest and
      // deletes the original key — and forces status='draft' for the draft button.
      case 'SAVE_CONTEST_DRAFT':
        return { ...(model ?? {}), status: 'draft' };
      case 'REQUEST_CONTEST_CLIENT':
        return model ?? {};
      case 'DUPLICATE_CONTEST':
        return { contest_id: model?.contest_id ?? model?.id };

      case 'UPDATE_LANGUAGE':
        return { language: model?.language ?? model };

      // getAllById passes its params object straight through as `model` — already
      // { contest_id } / { contest_id, name, start_date, end_date, page, per_page }.
      case 'QR_INSTANCE_LIST':
      case 'WEB_QR_REPORT':
        return model ?? {};

      // getByCustomName(key, name, id) put the pair in the query string on Xano, so the
      // NAME arrives as `model` and the VALUE as `extra`.
      case 'GET_Total_PARTICIPANT':
        return { contest_id: this.idOf(extra, model?.contest_id, model) };

      // create()'s payload — { contest_id, label, placement_type, expires_at, max_participants,
      // notes, base_url } on create, { id, label, is_active, expires_at, clear_expiry,
      // max_participants, clear_max_participants, notes, base_url } on update — goes through
      // as-is, exactly like QUIZ_UPSERT above. The participation limit is sent here; storing it
      // (and returning it from QR_INSTANCE_LIST) is the backend action's job.
      case 'QR_INSTANCE_CREATE':
      case 'QR_INSTANCE_UPDATE':
        return model ?? {};

      default:
        return model ?? {};
    }
  }

  // ── response: microservice `data` → the body the component reads ───────────
  private fromMicro(endpoint: string, data: any): any {
    switch (endpoint) {
      // Xano's active_plans / billing_payment returned a BARE ARRAY of plans, and the pricing
      // screens iterate the response directly. 2400 already returns the array; 2300 wraps it.
      case 'GET_SUBSCRIPTION_PLAN':
        return data?.plans ?? [];
      case 'GET_BILLING_PLANS':
        return Array.isArray(data) ? data : (data?.plans ?? []);

      // Xano's subusers list was a bare array too; the microservice pages it.
      case 'GET_SUBUSERS':
        return data?.items ?? [];

      // The Branches screen maps a bare array of branch rows.
      case 'CLIENT_BRANCH_LIST':
        return Array.isArray(data) ? data : (data?.items ?? []);

      // The Roles screens read { items } (the page envelope 1033 already returns).
      case 'GET_SUB_USERS_BY_PARENT':
        return { ...(data || {}), items: data?.items ?? [] };

      // Xano returned the role list and the page list as BARE ARRAYS.
      case 'CLIENT_ROLES':
      case 'CLIENT_PAGES':
        return Array.isArray(data) ? data : (data?.items ?? []);
      // 2365 answers { client_user_id, role_id }; the panel only checks that it did not throw.
      case 'ASSIGN_SUBUSER_ROLE':
        return { success: true, sub_user_id: data?.client_user_id, role_id: data?.role_id ?? 0 };

      // Activation just returns { message } — component reads response.isSuccess.
      case 'ACTIVATE_SUBUSER':
        return { isSuccess: true, ...(data || {}) };

      // The company form builds its own tree from parent_id, so this stays FLAT.
      case 'GET_SECTORS':
      case 'GET_BUSINESS_SUBJECTS':
        return Array.isArray(data) ? data : (data?.items ?? []);

      // The profile page destructures response[0], so this stays a bare array of one company.
      case 'GET_USER_COMPANY':
        return Array.isArray(data) ? data : data ? [data] : [];

      // The profile page reads response.output.isSuccess — Xano's wrapper. Returning `data` bare
      // made a SUCCESSFUL password change report "password reset failed", because
      // response.output was undefined. A real failure throws earlier and never reaches here.
      case 'USER_RESET_PASSWORD':
        return { output: data ?? { isSuccess: true } };

      // The bell reads this as a bare array too: `if (response && response.length > 0)`.
      case 'GET_NOTIFICATION':
      // Xano returned these as BARE ARRAYS and the contest form iterates them directly.
      case 'CATEGORIES_LOOKUP':
      case 'CONTEST_CATEGORY_LOOKUP':
        return Array.isArray(data) ? data : (data?.items ?? []);

      // The Quiz grid does `this.quizzes = response || []` and the contest form's quiz dropdown
      // iterates the response directly, so both stay BARE ARRAYS — as Xano's quizzes_client and
      // quizzes_look_up_client were. GET_QUIZ_BY_ID, QUIZ_UPSERT and DELETE_QUIZ are the bare
      // object / bare `{success, message}` Xano returned, which is `data` — they fall through.
      case 'GET_QUIZZES':
      case 'GET_QUIZZES_LOOKUP':
        return Array.isArray(data) ? data : (data?.items ?? []);

      // TEST_PAGINATION is consumed as `{items, meta}` with no wrapper, GET_SINGLE_CONTEST as a
      // bare contest object, GET_ALL_CONTESTS as any of three envelopes, and the overviews as
      // flat numbers — all of which is exactly `data`, so they fall through to the default.

      // Xano: { data: { isSuccess, status } } — kept, because the payment-return page switches
      // on `status` and the components were written against that nesting.
      case 'CHECK_PAYMENT':
      case 'CHECK_PAYMENT_BY_USER':
      case 'CHECK_SDK_PAYMENT':
        return { data: { isSuccess: data?.status === 'paid', status: data?.status }, ...data };

      // The override history is read as `response.items` (or a bare array); 2471 returns the page.
      case 'WINNER_OVERRIDE_AUDIT_LOG':
        return data?.items ?? [];

      // The QR participants drawer reads the page envelope as-is; the numbers/status/enable are
      // read as a bare array / flag object by CompanionService, which normalises them itself.
      case 'COMPANION_NUMBERS_LIST':
        return Array.isArray(data) ? data : (data?.items ?? []);

      // Only a hash is stored, so the list carries a masked key. Handing that to a caller that
      // wants the whole key would be a lie, so this key answers with the key's METADATA and an
      // empty `api_key` — BranchesService.getApiKey() then yields '' and the screen renders.
      case 'CLIENT_API_KEY': {
        const first = Array.isArray(data) ? data[0] : null;
        return { api_key: '', key_prefix: first?.key_prefix ?? '', client_name: first?.client_name ?? '' };
      }

      // Already flat: 2314 returns planName / planTotalContests / totalContests /
      // activeContests / finishedContests / leftContests at the top level, which is exactly
      // what the card binds to. The `...data.subscription` spread is kept for the case where
      // this constant is repointed at 2302 again, where the subscription is nested.
      case 'SUBSCRIPTION_DETAIL':
        return { ...data, ...(data?.subscription || {}) };

      case 'PAYMENT_REPORT':
        return { isSuccess: true, message: 'Records fetched successfully.', data: data };

      // The Developer page reads res[0].api_key, as it did from Xano's bare array.
      case 'GET_DEVELOPER_API_KEY':
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return data ? [data] : [];

      // The page reads app.api_keys[0].api_key and lower-case platform/status words. Only the
      // key's prefix is stored, so a key minted in this session is shown in full (it can be
      // copied once, as on Xano) and an older one as its masked prefix.
      case 'SDK_APPLICATIONS_LIST': {
        const rows = Array.isArray(data) ? data : (data?.items ?? []);
        return rows.map((app: any) => {
          const platform = String(app?.platform ?? '').toLowerCase();
          return {
            ...app,
            platform: platform === 'sdk' ? 'all' : platform,
            status: String(app?.status ?? '').toLowerCase(),
            api_keys: (app?.api_keys || []).map((k: any) => ({
              ...k,
              api_key: this.mintedSdkKeys[String(app.id)] ?? (k?.key_prefix ? `${k.key_prefix}••••••••••••` : null),
            })),
          };
        });
      }

      case 'SDK_GENERATE_API_KEY':
        if (data?.api_key && data?.application?.id) this.mintedSdkKeys[String(data.application.id)] = data.api_key;
        return data;

      default:
        return data;
    }
  }

  /** Raw SDK keys minted this session, by application id — the server only keeps the hash. */
  private mintedSdkKeys: Record<string, string> = {};

  /** Keep the microservice token wherever the flow produced one. */
  private captureToken(data: any): void {
    const token = data?.user?.authToken ?? data?.authToken;
    if (token) localStorage.setItem(MS_TOKEN_KEY, token);
  }

  /** Called on logout so a stale microservice token cannot outlive the session. */
  clearToken(): void {
    localStorage.removeItem(MS_TOKEN_KEY);
  }
}
